import { useCallback, useRef } from "react";
import { GroupChildrenControls } from "./GroupChildrenLoader";

/**
 * Registry of the per-group pagination controls behind the "Load more" and
 * "Retry" rows.
 *
 * Each entry is owned by the `GroupChildrenLoader` that registered it and is
 * dropped when that loader unmounts. There is deliberately no bulk reset: the
 * table resets its loaded rows on sort change without remounting the loaders
 * (the groups query key excludes `sort`), so clearing the registry from the
 * outside would strand every control with nothing left to re-register it.
 */
export const useGroupChildrenControls = () => {
    const controlsRef = useRef<Record<string, GroupChildrenControls>>({});

    const registerControls = useCallback(
        (groupKey: string, controls: GroupChildrenControls) => {
            controlsRef.current[groupKey] = controls;
        },
        [],
    );

    const unregisterControls = useCallback((groupKey: string) => {
        delete controlsRef.current[groupKey];
    }, []);

    const loadMore = useCallback((groupKey: string) => {
        controlsRef.current[groupKey]?.fetchNextPage();
    }, []);

    const retry = useCallback((groupKey: string) => {
        controlsRef.current[groupKey]?.retry();
    }, []);

    return { registerControls, unregisterControls, loadMore, retry };
};
